import { useEffect, useState } from "react";
import { Bot, Save, Share2 } from "lucide-react";
import { useTheme } from "next-themes";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminSocialSubNav } from "@/components/admin/AdminSocialSubNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

interface LunaSettings {
  system_prompt: string;
  dm_auto_reply: boolean;
  comment_auto_reply: boolean;
  app_cta_frequency: number;
  tone: string;
}

const TONES = [
  { value: "warm", label: "Sıcak", desc: "Samimi ve destekleyici" },
  { value: "mystical", label: "Mistik", desc: "Gizemli ve derin" },
  { value: "professional", label: "Profesyonel", desc: "Resmi ve net" },
];

export default function AdminSocialLunaSettings() {
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const authenticated = !!sessionQuery.data?.authenticated;
  const configured = !!sessionQuery.data?.configured;
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  const [settings, setSettings] = useState<LunaSettings>({
    system_prompt: "",
    dm_auto_reply: true,
    comment_auto_reply: true,
    app_cta_frequency: 3,
    tone: "warm",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!authenticated) return;
    adminFetch("/api/admin/social/luna-settings")
      .then((r) => r.json())
      .then((json) => {
        if (json.ok) setSettings(json.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [authenticated]);

  async function handleSave() {
    setSaving(true);
    try {
      const r = await adminFetch("/api/admin/social/luna-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      const json = await r.json();
      if (json.ok) {
        toast({ title: "Luna ayarları kaydedildi" });
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
    } finally {
      setSaving(false);
    }
  }

  if (sessionQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-slate-500 dark:text-slate-300" />
                <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                  Sosyal Medya
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <AdminSocialSubNav />

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
            {loading ? (
              <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
              </div>
            ) : (
              <>
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                      Luna Ayarları
                    </div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      AI kişilik ve otomasyon tercihlerini yapılandır
                    </div>
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                    {saving ? (
                      <span className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Kaydediliyor
                      </span>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Kaydet
                      </>
                    )}
                  </Button>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Sol: Prompt */}
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="mb-3 flex items-center gap-2">
                        <Bot className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">System Prompt</h2>
                      </div>
                      <p className="mb-3 text-xs text-muted-foreground">
                        Luna'nın tüm yanıtlarının temelini oluşturan kişilik ve talimat metni.
                      </p>
                      <Textarea
                        className="min-h-[320px] border-slate-200 bg-slate-50 font-mono text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        value={settings.system_prompt}
                        onChange={(e) => setSettings((s) => ({ ...s, system_prompt: e.target.value }))}
                        placeholder="Luna'nın system promptunu buraya yaz…"
                      />
                    </div>

                    {/* Ton seçici */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Konuşma Tonu</h2>
                      <div className="grid grid-cols-3 gap-2">
                        {TONES.map((t) => (
                          <button
                            key={t.value}
                            onClick={() => setSettings((s) => ({ ...s, tone: t.value }))}
                            className={`rounded-xl border p-3 text-left transition-colors ${
                              settings.tone === t.value
                                ? "border-purple-400/50 bg-purple-50 text-purple-700 dark:bg-purple-600/20 dark:text-purple-200"
                                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400 dark:hover:border-slate-600"
                            }`}
                          >
                            <div className="text-xs font-medium">{t.label}</div>
                            <div className="mt-0.5 text-[10px] opacity-70">{t.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Sağ: Otomasyon */}
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <h2 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Otomasyon Ayarları
                      </h2>
                      <div className="space-y-4">
                        <Toggle
                          label="DM Otomatik Yanıt"
                          desc="Gelen DM'lere Luna otomatik yanıt versin"
                          checked={settings.dm_auto_reply}
                          onChange={(v) => setSettings((s) => ({ ...s, dm_auto_reply: v }))}
                        />
                        <Toggle
                          label="Yorum Otomatik Yanıt"
                          desc="Gelen yorumlara Luna otomatik yanıt versin"
                          checked={settings.comment_auto_reply}
                          onChange={(v) => setSettings((s) => ({ ...s, comment_auto_reply: v }))}
                        />
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <h2 className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Uygulama CTA Frekansı
                      </h2>
                      <p className="mb-4 text-xs text-muted-foreground">
                        Her kaç mesajda bir Soul Circles uygulamasına yönlendirme yapılsın
                      </p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                          <span>Her mesajda</span>
                          <span className="font-medium text-slate-700 dark:text-slate-200">
                            Her {settings.app_cta_frequency}. mesajda
                          </span>
                          <span>Her 5. mesajda</span>
                        </div>
                        <input
                          type="range"
                          min={1}
                          max={5}
                          value={settings.app_cta_frequency}
                          onChange={(e) =>
                            setSettings((s) => ({ ...s, app_cta_frequency: Number(e.target.value) }))
                          }
                          className="w-full accent-blue-500"
                        />
                        <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <span key={n}>{n}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-700 dark:border-amber-700/30 dark:bg-amber-950/20 dark:text-amber-300">
                      <strong>Not:</strong> System prompt değişiklikleri sonraki API çağrısından itibaren geçerli olur.
                      Mevcut aktif konuşmaları etkilemez.
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm text-slate-700 dark:text-slate-200">{label}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
          checked ? "bg-blue-600" : "bg-slate-200 dark:bg-slate-700"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}
